// client/src/pages/business/Favorites.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, ShoppingCart, Search, X, Package, ChevronDown, Filter, Tag, Trash2, CheckCircle, ArrowUpDown } from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import StorefrontBanner, { useStorefrontContext } from '../../components/common/StorefrontBanner';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import toast from 'react-hot-toast';
import { favoritesAPI, productDiscountsAPI, flashOffersAPI, catalogDiscountsAPI, discountsAPI, businessAPI } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { getImageUrl } from '../../utils/imageHelper';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';

const PLACEHOLDER_PRODUCT = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22 viewBox=%220 0 200 200%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22200%22 height=%22200%22 rx=%2212%22/%3E%3Cpath fill=%22%23d1d5db%22 d=%22M70 80h60v8H70zm0 16h60v8H70zm0 16h40v8H70z%22/%3E%3Ccircle fill=%22%23e5e7eb%22 cx=%22100%22 cy=%22140%22 r=%2214%22/%3E%3C/svg%3E';
const PLACEHOLDER_LOGO = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2248%22 height=%2248%22 viewBox=%220 0 48 48%22%3E%3Crect fill=%22%23dbeafe%22 width=%2248%22 height=%2248%22 rx=%2224%22/%3E%3Ctext x=%2224%22 y=%2230%22 font-family=%22Arial%22 font-size=%2220%22 fill=%22%233b82f6%22 text-anchor=%22middle%22%3EW%3C/text%3E%3C/svg%3E';

const Favorites = () => {
  const { t } = useTranslation('business');
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isFromStorefront, wholesalerId: storefrontWholesalerId } = useStorefrontContext();

  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWholesaler, setSelectedWholesaler] = useState('all');
  const [quantities, setQuantities] = useState({});
  const [expandedWholesalers, setExpandedWholesalers] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [productDiscounts, setProductDiscounts] = useState({});
  const [flashOfferMap, setFlashOfferMap] = useState({}); // { productId: { offerPrice, offerId, offerTitle } }
  const [customerDiscounts, setCustomerDiscounts] = useState({}); // { [wholesalerId]: { percentage } }
  const [catalogDiscounts, setCatalogDiscounts] = useState({}); // { [wholesalerId]: [...rawDiscounts] }
  const [sortBy, setSortBy] = useState('name');

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, icon: 'warning', confirmColor: 'red' });
  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    if (storefrontWholesalerId) setShowFilters(true);
  }, [storefrontWholesalerId]);

  useEffect(() => { fetchFavorites(); }, []);

  useEffect(() => {
    if (storefrontWholesalerId && selectedWholesaler === 'all') {
      setSelectedWholesaler(storefrontWholesalerId);
    }
  }, [storefrontWholesalerId]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const response = await favoritesAPI.getAll();
      const favs = response.data.favorites || [];
      setFavorites(favs);

      const initialQuantities = {};
      favs.forEach(fav => {
        if (fav.productId) initialQuantities[fav.productId._id] = fav.productId.pricing?.minOrderQuantity || 1;
      });
      setQuantities(initialQuantities);

      // Auto-expand all wholesaler groups
      const allWids = new Set(
        favs.filter(f => f.productId?.wholesalerId?._id).map(f => f.productId.wholesalerId._id)
      );
      setExpandedWholesalers(allWids);

      // Fetch product discounts, customer discounts, catalog discounts per wholesaler
      const uniqueWids = [...allWids];
      const pdMap = {};
      const cdMap = {};
      const catMap = {};

      // Get business ID for customer discount lookup
      let businessId = null;
      try {
        const profileRes = await businessAPI.getProfile();
        businessId = profileRes.data.business?._id;
      } catch { /* silent */ }

      await Promise.all(uniqueWids.map(async (wid) => {
        const widStr = String(wid);
        try {
          const res = await productDiscountsAPI.getMyDiscounts(widStr);
          if (res.data.success && res.data.discounts && typeof res.data.discounts === 'object') {
            pdMap[widStr] = res.data.discounts;
          }
        } catch { /* silent */ }

        if (businessId) {
          try {
            const res = await discountsAPI.getCustomerDiscountsForBusiness(businessId, widStr);
            if (res.data.success && res.data.hasDiscounts) {
              const totalPct = res.data.discounts.reduce((sum, d) => sum + (d.percentage || 0), 0);
              cdMap[widStr] = { percentage: Math.min(totalPct, 100) };
            }
          } catch { /* silent */ }
        }

        try {
          const res = await catalogDiscountsAPI.getForWholesaler(widStr);
          if (res.data.discounts?.length > 0) {
            catMap[widStr] = res.data.discounts;
          }
        } catch { /* silent */ }
      }));

      setProductDiscounts(pdMap);
      setCustomerDiscounts(cdMap);
      setCatalogDiscounts(catMap);

      // Fetch active flash offers to show flash prices on favorites
      try {
        const foRes = await flashOffersAPI.getAvailable();
        const foMap = {};
        (foRes.data.offers || []).forEach(offer => {
          (offer.items || []).forEach(item => {
            if (item.productId && item.offerPrice != null) {
              foMap[String(item.productId)] = {
                offerPrice: item.offerPrice,
                originalPrice: item.originalPrice,
                offerId: offer._id,
                offerTitle: offer.title,
                discountType: offer.discountType,
                discountPercentage: offer.discountPercentage
              };
            }
          });
        });
        setFlashOfferMap(foMap);
      } catch { /* silent */ }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const wholesalers = [...new Set(
    favorites
      .filter(fav => fav.productId?.wholesalerId)
      .map(fav => JSON.stringify({
        id: fav.productId.wholesalerId._id,
        name: fav.productId.wholesalerId.companyName,
        logo: fav.productId.wholesalerId.logo || null
      }))
  )].map(str => JSON.parse(str));

  const filteredFavorites = useMemo(() => {
    let result = favorites.filter(fav => {
      if (!fav.productId) return false;
      const matchesSearch = fav.productId.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           fav.productId.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           fav.nickname?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesWholesaler = selectedWholesaler === 'all' ||
                               fav.productId.wholesalerId?._id === selectedWholesaler;
      return matchesSearch && matchesWholesaler;
    });

    // Sort
    result = [...result].sort((a, b) => {
      const pA = a.productId;
      const pB = b.productId;
      if (!pA || !pB) return 0;
      if (sortBy === 'name') {
        return (pA.name || '').localeCompare(pB.name || '', 'el');
      }
      if (sortBy === 'price') {
        return (pA.pricing?.unitPrice || 0) - (pB.pricing?.unitPrice || 0);
      }
      if (sortBy === 'discount') {
        const getDisc = (fav) => {
          const wid = String(fav.productId.wholesalerId?._id || '');
          const pid = String(fav.productId._id);
          return productDiscounts[wid]?.[pid]?.percentage || 0;
        };
        return getDisc(b) - getDisc(a); // highest discount first
      }
      return 0;
    });

    return result;
  }, [favorites, searchTerm, selectedWholesaler, sortBy, productDiscounts]);

  // Bulk selection on the flat favorites list
  const {
    selectedIds, handleItemClick, selectAll, clearSelection,
    dragProps, isDragging, dragRect
  } = useRangeSelect(filteredFavorites);

  const groupedFavorites = filteredFavorites.reduce((acc, fav) => {
    if (!fav.productId?.wholesalerId) return acc;
    const wholesalerId = fav.productId.wholesalerId._id;
    if (!acc[wholesalerId]) {
      acc[wholesalerId] = { wholesaler: fav.productId.wholesalerId, products: [] };
    }
    acc[wholesalerId].products.push(fav);
    return acc;
  }, {});

  const toggleWholesaler = (wholesalerId) => {
    const newExpanded = new Set(expandedWholesalers);
    if (newExpanded.has(wholesalerId)) newExpanded.delete(wholesalerId);
    else newExpanded.add(wholesalerId);
    setExpandedWholesalers(newExpanded);
  };

  const showToast = (msg) => toast.success(msg);

  const handleAddToCart = (product) => {
    const minQty = product.pricing?.minOrderQuantity || 1;
    const quantity = quantities[product._id] || minQty;
    const fo = flashOfferMap[String(product._id)];
    addToCart({
      ...product,
      wholesaler: product.wholesalerId,
      wholesalerName: product.wholesalerId?.companyName,
      ...(fo && { flashOfferPrice: fo.offerPrice, flashOfferId: fo.offerId, flashOfferTitle: fo.offerTitle })
    }, Math.max(minQty, quantity));
    showToast(t('cart.addedToCart'));
  };

  const handleAddAllToCart = (products) => {
    let addedCount = 0;
    products.forEach(fav => {
      if (fav.productId && fav.productId.availability?.inStock !== false) {
        const minQty = fav.productId.pricing?.minOrderQuantity || 1;
        const quantity = Math.max(minQty, quantities[fav.productId._id] || minQty);
        const fo = flashOfferMap[String(fav.productId._id)];
        addToCart({
          ...fav.productId,
          wholesaler: fav.productId.wholesalerId,
          wholesalerName: fav.productId.wholesalerId?.companyName,
          ...(fo && { flashOfferPrice: fo.offerPrice, flashOfferId: fo.offerId, flashOfferTitle: fo.offerTitle })
        }, quantity);
        addedCount++;
      }
    });
    if (addedCount > 0) showToast(`${addedCount} ${t('cart.addedToCart')}`);
  };

  // Bulk actions
  const handleBulkRemove = () => {
    const count = selectedIds.size;
    setConfirmModal({
      isOpen: true,
      title: t('favorites.bulkRemoveTitle', 'Remove from Favorites'),
      message: t('favorites.bulkRemoveMessage', 'Remove {{count}} selected items from your favorites?', { count }),
      icon: 'danger',
      confirmLabel: t('favorites.remove', 'Remove'),
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          const productIds = favorites.filter(f => selectedIds.has(f._id)).map(f => f.productId._id);
          await favoritesAPI.bulkRemove(productIds);
          clearSelection();
          fetchFavorites();
        } catch (err) {
          console.error('Bulk remove error:', err);
        }
      }
    });
  };

  const handleBulkAddToCart = () => {
    const selectedFavs = favorites.filter(f => selectedIds.has(f._id) && f.productId && f.productId.availability?.inStock !== false);
    let addedCount = 0;
    selectedFavs.forEach(fav => {
      const minQty = fav.productId.pricing?.minOrderQuantity || 1;
      const quantity = Math.max(minQty, quantities[fav.productId._id] || minQty);
      const fo = flashOfferMap[fav.productId._id];
      addToCart({
        ...fav.productId,
        wholesaler: fav.productId.wholesalerId,
        wholesalerName: fav.productId.wholesalerId?.companyName,
        ...(fo && { flashOfferPrice: fo.offerPrice, flashOfferId: fo.offerId, flashOfferTitle: fo.offerTitle })
      }, quantity);
      addedCount++;
    });
    if (addedCount > 0) {
      showToast(`${addedCount} ${t('cart.addedToCart')}`);
      clearSelection();
    }
  };

  // Helper to get product discount for an item
  const getProductDiscount = (fav) => {
    const wid = String(fav.productId.wholesalerId?._id || '');
    const pid = String(fav.productId._id);
    return productDiscounts[wid]?.[pid] || null;
  };

  // Helper to compute catalog discount % for a product
  const getCatalogDiscountPct = (product, wholesalerId) => {
    const discounts = catalogDiscounts[String(wholesalerId)];
    if (!discounts?.length) return 0;
    let totalPct = 0;
    discounts.forEach(d => {
      if (d.type === 'flat') {
        totalPct += d.percentage || 0;
      } else if (d.type === 'targeted') {
        const matches =
          (d.targetType === 'category' && (product.category || '').toLowerCase() === (d.targetValue || '').toLowerCase()) ||
          (d.targetType === 'brand' && (product.brand || '').toLowerCase() === (d.targetValue || '').toLowerCase());
        if (matches) totalPct += d.targetPercentage || 0;
      }
    });
    return Math.min(totalPct, 90);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
        <p className="text-gray-500 font-medium">{t('favorites.loading')}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <StorefrontBanner />

      <main
        className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8 pb-24 mt-14 lg:mt-0"
        {...dragProps}
        ref={dragProps.ref}
        style={{ userSelect: isDragging ? 'none' : undefined }}
      >
        <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-xl">
                <Heart className="w-6 h-6 text-red-500 fill-current" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('favorites.title')}</h1>
                <p className="text-sm text-gray-500">{t('favorites.items', { count: favorites.length })}</p>
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-sm"
            >
              <Filter className="w-4 h-4" />
              {t('wholesalers.filters')}
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className={`mb-6 space-y-3 ${showFilters ? 'block' : 'hidden lg:block'}`}>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('favorites.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <select
              value={selectedWholesaler}
              onChange={(e) => setSelectedWholesaler(e.target.value)}
              className="sm:w-64 px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
            >
              <option value="all">{t('favorites.allWholesalers')} ({wholesalers.length})</option>
              {wholesalers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            {/* Sort dropdown */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sm:w-48 px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm appearance-none pr-8"
              >
                <option value="name">{t('favorites.sortByName', 'Name')}</option>
                <option value="price">{t('favorites.sortByPrice', 'Price')}</option>
                <option value="discount">{t('favorites.sortByDiscount', 'Discount %')}</option>
              </select>
              <ArrowUpDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredFavorites.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm || selectedWholesaler !== 'all' ? t('wholesalers.noMatchingSearch') : t('favorites.noFavorites')}
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto px-4">
              {searchTerm || selectedWholesaler !== 'all' ? t('wholesalers.tryAdjusting') : t('favorites.startBrowsing')}
            </p>
            {!searchTerm && selectedWholesaler === 'all' && (
              <button
                onClick={() => navigate('/wholesalers')}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                {t('wholesalers.viewProducts')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedFavorites).map(([wholesalerId, group]) => {
              const isExpanded = expandedWholesalers.has(wholesalerId);
              const wholesalerLogo = group.wholesaler.logo;
              const logoUrl = wholesalerLogo ? getImageUrl(wholesalerLogo) : null;

              return (
                <div key={wholesalerId} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Wholesaler Header */}
                  <button
                    onClick={() => toggleWholesaler(wholesalerId)}
                    className="w-full px-4 sm:px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={logoUrl || PLACEHOLDER_LOGO}
                        alt={group.wholesaler.companyName}
                        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border-2 border-gray-100 bg-white"
                        onError={(e) => {
                          if (!e.target.dataset.errorHandled) {
                            e.target.dataset.errorHandled = 'true';
                            e.target.src = PLACEHOLDER_LOGO;
                          }
                        }}
                      />
                      <div className="text-left">
                        <h2 className="text-base sm:text-lg font-bold text-gray-900">{group.wholesaler.companyName}</h2>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs text-gray-500">{group.products.length} {group.products.length === 1 ? t('favorites.item', 'item') : t('favorites.itemsLabel', 'items')}</p>
                          {customerDiscounts[wholesalerId]?.percentage > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-green-600 text-white rounded">
                              <Tag className="w-2.5 h-2.5" />
                              {customerDiscounts[wholesalerId].percentage}% {t('favorites.customerDiscount', 'customer discount')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="border-t border-gray-100">
                      {/* Add All to Cart */}
                      <div className="px-4 sm:px-5 pt-4 pb-2">
                        <button
                          onClick={() => handleAddAllToCart(group.products)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          {t('favorites.addToCart')}
                        </button>
                      </div>

                      {/* Product Grid — matches WholesalerDetail card sizing */}
                      <div className="px-3 sm:px-5 pb-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4 md:gap-6 mt-2">
                        {group.products.map(fav => {
                          const product = fav.productId;
                          const hidePricing = product.wholesalerId?.hidePricing;
                          const pd = getProductDiscount(fav);
                          const fo = flashOfferMap[String(product._id)];
                          const catPct = getCatalogDiscountPct(product, wholesalerId);
                          // Use the group's wholesalerId key directly — same key as the header badge
                          const cdPct = customerDiscounts[wholesalerId]?.percentage || 0;

                          // Compute effective display price (mirrors WholesalerDetail logic)
                          const originalPrice = product.pricing?.unitPrice || product.pricing?.basePrice || 0;
                          let effectivePrice = originalPrice;
                          let priceColor = 'text-green-600';
                          let showFlashBadge = false;
                          if (fo) {
                            // Flash offer = final price, no other discounts on top
                            effectivePrice = fo.offerPrice;
                            priceColor = 'text-red-600';
                            showFlashBadge = true;
                          } else if (pd) {
                            const effectivePct = Math.min(Math.max(pd.percentage, cdPct) + catPct, 100);
                            effectivePrice = Math.round(originalPrice * (1 - effectivePct / 100) * 100) / 100;
                            priceColor = 'text-purple-700';
                          } else if (catPct > 0) {
                            const totalPct = Math.min(catPct + cdPct, 100);
                            effectivePrice = Math.round(originalPrice * (1 - totalPct / 100) * 100) / 100;
                            priceColor = 'text-teal-700';
                          } else if (cdPct > 0) {
                            effectivePrice = Math.round(originalPrice * (1 - cdPct / 100) * 100) / 100;
                            priceColor = 'text-emerald-600';
                          }
                          const hasAnySaving = effectivePrice < originalPrice;
                          const productImage = getImageUrl(product.images?.[0]);
                          const stockStatus = product.stockStatus || 'in_stock';
                          const isOutOfStock = product.availability?.inStock === false || stockStatus === 'out_of_stock';
                          const isFavSelected = selectedIds.has(fav._id);

                          return (
                            <div
                              key={fav._id}
                              data-select-id={fav._id}
                              className={`border rounded-lg overflow-hidden hover:shadow-lg transition-shadow relative ${
                                isFavSelected ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-gray-200'
                              }`}
                            >
                              {/* Selection checkbox — top-left */}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleItemClick(fav._id, e); }}
                                className={`absolute top-1 left-1 z-20 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  isFavSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 hover:border-indigo-400 bg-white/80'
                                }`}
                              >
                                {isFavSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                              </button>

                              {/* Product Image */}
                              <div className="relative h-24 sm:h-32 lg:h-40 bg-gray-100">
                                {productImage ? (
                                  <img
                                    src={productImage}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                    onClick={() => navigate(`/products/${product._id}`)}
                                    style={{ cursor: 'pointer' }}
                                    onError={(e) => {
                                      if (!e.target.dataset.errorHandled) {
                                        e.target.dataset.errorHandled = 'true';
                                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-8 h-8 sm:w-12 sm:h-12 lg:w-14 lg:h-14 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg></div>';
                                      }
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center cursor-pointer" onClick={() => navigate(`/products/${product._id}`)}>
                                    <Package className="w-8 h-8 sm:w-12 sm:h-12 lg:w-14 lg:h-14 text-gray-300" />
                                  </div>
                                )}
                                {/* Favorite button */}
                                <div className="absolute top-1 right-1 z-10">
                                  <FavoriteButton productId={product._id} size="sm" />
                                </div>
                                {/* Discount badge */}
                                {hasAnySaving && !hidePricing && (
                                  <div className="absolute top-7 left-1 z-10">
                                    {fo ? (
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-md shadow-sm">
                                        ⚡ -{Math.round(((originalPrice - fo.offerPrice) / originalPrice) * 100)}%
                                      </span>
                                    ) : (
                                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold text-white rounded-md shadow-sm ${pd ? 'bg-purple-600' : catPct > 0 ? 'bg-teal-600' : 'bg-emerald-600'}`}>
                                        <Tag className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                        -{Math.round(((originalPrice - effectivePrice) / originalPrice) * 100)}%
                                      </span>
                                    )}
                                  </div>
                                )}
                                {/* Out of stock badge */}
                                {isOutOfStock && (
                                  <div className="absolute bottom-2 left-2">
                                    <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-red-100 text-red-800 rounded-full text-[10px] sm:text-xs font-medium">
                                      {t('favorites.outOfStock', 'Out of Stock')}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Product Details */}
                              <div className="p-2 sm:p-3">
                                <h3
                                  className="text-xs sm:text-sm font-semibold text-gray-900 mb-0.5 sm:mb-1 cursor-pointer hover:text-blue-600 line-clamp-2"
                                  onClick={() => navigate(`/products/${product._id}`)}
                                >
                                  {product.name}
                                </h3>
                                {product.sku && (
                                  <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5 hidden sm:block">SKU: {product.sku}</p>
                                )}

                                {/* Price with Stock Status Dot */}
                                <div className="mb-1 sm:mb-2 flex items-center gap-1.5">
                                  <span
                                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                      stockStatus === 'in_stock' ? 'bg-green-500' :
                                      stockStatus === 'low_stock' ? 'bg-yellow-500' :
                                      stockStatus === 'out_of_stock' ? 'bg-red-500' :
                                      'bg-green-500'
                                    }`}
                                  />
                                  <div>
                                    {hidePricing ? (
                                      <span className="text-[10px] sm:text-xs text-gray-500 italic">{t('myOrders.pricingHidden', 'Κατόπιν συμφωνίας')}</span>
                                    ) : hasAnySaving ? (
                                      <div className="flex items-baseline gap-1 flex-wrap">
                                        <span className={`text-sm sm:text-base font-bold ${priceColor}`}>
                                          €{effectivePrice.toFixed(2)}
                                        </span>
                                        <span className="text-[10px] sm:text-xs text-gray-400 line-through">
                                          €{originalPrice.toFixed(2)}
                                        </span>
                                        {showFlashBadge && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[9px] font-bold rounded">FLASH</span>}
                                        <span className="text-[10px] sm:text-xs text-gray-500">/ {product.pricing?.unit}</span>
                                      </div>
                                    ) : (
                                      <div className="flex items-baseline gap-1">
                                        <span className="text-base sm:text-lg font-bold text-green-600">
                                          €{originalPrice.toFixed(2)}
                                        </span>
                                        <span className="text-xs sm:text-sm text-gray-500">/ {product.pricing?.unit}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Quantity + Add to Cart */}
                                {!isOutOfStock && (
                                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                                    <input
                                      type="number"
                                      min={product.pricing?.minOrderQuantity || 1}
                                      step={product.pricing?.minOrderQuantity || 1}
                                      value={quantities[product._id] || product.pricing?.minOrderQuantity || 1}
                                      onChange={(e) => {
                                        const minQty = product.pricing?.minOrderQuantity || 1;
                                        const val = parseInt(e.target.value) || minQty;
                                        setQuantities({ ...quantities, [product._id]: Math.max(minQty, val) });
                                      }}
                                      className="w-12 sm:w-16 border border-gray-300 rounded-md p-1.5 sm:p-2 text-center text-sm"
                                    />
                                    <button
                                      onClick={() => handleAddToCart(product)}
                                      className="flex-1 bg-blue-600 text-white px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                      {t('productDetails.addToCart')}
                                    </button>
                                  </div>
                                )}

                                {/* View Details */}
                                <button
                                  onClick={() => navigate(`/products/${product._id}`)}
                                  className="block w-full text-center text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                >
                                  {t('wholesalerDetail.viewDetails', 'View Details')} →
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Floating Action Bar for bulk actions */}
      <FloatingActionBar
        count={selectedIds.size}
        onClear={clearSelection}
        onSelectAll={selectAll}
      >
        <button
          onClick={handleBulkAddToCart}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
        >
          <ShoppingCart className="w-4 h-4" />
          {t('favorites.bulkAddToCart', 'Add to Cart')}
        </button>
        <button
          onClick={handleBulkRemove}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          {t('favorites.bulkRemove', 'Remove from Favorites')}
        </button>
      </FloatingActionBar>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon={confirmModal.icon}
        confirmLabel={confirmModal.confirmLabel}
        confirmColor={confirmModal.confirmColor}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
      />

      <style>{`
        @keyframes slide-down { from { opacity: 0; transform: translate(-50%, -100%); } to { opacity: 1; transform: translate(-50%, 0); } }
        .animate-slide-down { animation: slide-down 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default Favorites;
